/** A concrete step a wizard can render. `'result'` is deliberately excluded — it is the implicit state after the last configured step succeeds, never a step a caller places in the sequence. */
export type DataImportWizardStepId = 'file' | 'mapping' | 'preview';

/**
 * The public flow a wizard can be configured with. Restricted to the two
 * supported tuples — rather than `readonly DataImportWizardStepId[]` — so an
 * invalid order (mapping before file, preview before mapping, a repeated or
 * missing step) cannot be constructed at all: file and preview are always
 * present and in order, and mapping is either omitted or sits between them.
 */
export type DataImportWizardSteps =
  | readonly ['file', 'preview']
  | readonly ['file', 'mapping', 'preview'];

export const DEFAULT_DATA_IMPORT_WIZARD_STEPS: DataImportWizardSteps = [
  'file',
  'mapping',
  'preview',
];

/** The flow a wizard runs: the caller's configured sequence, or the default. */
export function resolveDataImportWizardSteps(
  steps?: DataImportWizardSteps,
): DataImportWizardSteps {
  return steps ?? DEFAULT_DATA_IMPORT_WIZARD_STEPS;
}

export function hasDataImportMappingStep(steps: DataImportWizardSteps) {
  return (steps as readonly DataImportWizardStepId[]).includes('mapping');
}

/** `undefined` for the first configured step — there is nothing to go back to. */
export function getPreviousDataImportWizardStep(
  steps: DataImportWizardSteps,
  step: DataImportWizardStepId,
): DataImportWizardStepId | undefined {
  const index = (steps as readonly DataImportWizardStepId[]).indexOf(step);
  return index > 0 ? steps[index - 1] : undefined;
}

/**
 * The numeric position `Stepper` needs, derived at its boundary so the rest
 * of the wizard can stay on semantic step ids. `'result'` maps past the last
 * step, which is how `Stepper` marks every configured step complete.
 */
export function getDataImportWizardStepIndex(
  steps: DataImportWizardSteps,
  step: DataImportWizardStepId | 'result',
): number {
  return step === 'result'
    ? steps.length
    : (steps as readonly DataImportWizardStepId[]).indexOf(step);
}
